import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, MoreHorizontal, ChevronLeft, ChevronRight, AlertTriangle, Plus } from "lucide-react";
import {
  DndContext, closestCorners, PointerSensor, KeyboardSensor,
  useSensor, useSensors, useDraggable, useDroppable,
} from "@dnd-kit/core";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MediaThumb, MediaCover } from "@/components/MediaThumb";
import { ImageThumbs } from "@/components/Lightbox";
import { formatMoney } from "@/lib/currency";
import { isLowStock } from "@/lib/stock";
import { MemberChip } from "@/lib/members";

/** Small amber "Low stock" pill, rendered when a record is at/below its reorder level. */
function LowStockBadge({ record, stockKeys, className = "" }) {
  if (!isLowStock(record.fields || {}, stockKeys)) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full ${className}`}
      data-testid={`low-stock-${record.record_number}`}
    >
      <AlertTriangle className="w-2.5 h-2.5" /> Low stock
    </span>
  );
}

// Normalize an image/file field value to a list of media ids.
function mediaIds(value) {
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((v) => (v && typeof v === "object" ? v.media_id : (typeof v === "string" ? v : null)))
    .filter(Boolean);
}

export function formatCellValue(field, value, opts = {}) {
  if (value === null || value === undefined || value === "") return "—";
  const unit = field.unit ? ` ${field.unit}` : "";
  switch (field.type) {
    case "boolean": return value ? "Yes" : "No";
    case "currency":
      return formatMoney(value);
    case "multi_select": return Array.isArray(value) ? value.join(", ") : String(value);
    case "longtext":
    case "richtext":
      return typeof value === "string" && value.length > 60 ? value.slice(0, 60) + "…" : value;
    case "number": return `${value}${unit}`;
    case "image": {
      const ids = mediaIds(value);
      if (!ids.length) return "—";
      const size = opts.thumbSize || 40;
      // Thumbnails are click-to-enlarge via the Lightbox. stopPropagation in
      // ImageThumbs keeps a click from also triggering a card/row navigation.
      return (
        <ImageThumbs
          ids={ids}
          size={size}
          renderThumb={(id, s) => <MediaThumb media={{ id, mime: "image/jpeg" }} size={s} />}
        />
      );
    }
    case "file": {
      const ids = mediaIds(value);
      if (!ids.length) return "—";
      return (
        <span className="text-xs text-muted-foreground">
          {ids.length} file{ids.length === 1 ? "" : "s"}
        </span>
      );
    }
    case "member":
      return value ? <MemberChip userId={value} /> : "—";
    case "checklist": {
      const items = Array.isArray(value) ? value : [];
      if (!items.length) return "—";
      const done = items.filter((i) => i && i.done).length;
      const pct = Math.round((done / items.length) * 100);
      return (
        <span className="inline-flex items-center gap-1.5">
          <span className="w-12 h-1.5 rounded-full bg-muted overflow-hidden inline-block align-middle">
            <span className="h-full block rounded-full bg-primary" style={{ width: `${pct}%` }} />
          </span>
          <span className="text-xs tabular-nums text-muted-foreground">{done}/{items.length}</span>
        </span>
      );
    }
    case "rollup":
    case "formula": {
      const disp = field.config?.display;
      const n = typeof value === "number" ? value : Number(value);
      if (Number.isNaN(n)) return String(value);
      if (disp === "currency") return formatMoney(n);
      if (disp === "percent") return `${Math.round(n)}%`;
      return `${(+n.toFixed(2)).toLocaleString()}${unit}`;
    }
    default: return `${String(value)}${unit}`;
  }
}

function CategoriesInline({ record, catsById }) {
  const first = record.category_ids?.[0] && catsById[record.category_ids[0]];
  if (!first) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="text-xs" title={first.path_names?.join(" › ")}>
      {first.name}
      {record.category_ids.length > 1 && (
        <span className="text-muted-foreground"> +{record.category_ids.length - 1}</span>
      )}
    </span>
  );
}

function TagsInline({ record, tagsById, max = 3 }) {
  const ids = record.tag_ids || [];
  return (
    <div className="flex flex-wrap gap-1">
      {ids.slice(0, max).map((tid) => {
        const t = tagsById[tid];
        if (!t) return null;
        return (
          <span key={tid}
            className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
            style={{ backgroundColor: (t.color || "#0d9488") + "1a", color: t.color || "#0d9488" }}>
            {t.name}
          </span>
        );
      })}
      {ids.length > max && <span className="text-[10px] text-muted-foreground">+{ids.length - max}</span>}
    </div>
  );
}

// Drag handle on a header's right edge. Resizes live, persists on release;
// double-click returns the column to automatic width.
function ResizeHandle({ colKey, thRef, onResize, onLive }) {
  const onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = thRef.current?.getBoundingClientRect().width || 120;
    let w = startW;
    const move = (ev) => {
      w = Math.max(60, Math.min(800, startW + ev.clientX - startX));
      onLive(colKey, w);
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      onResize && onResize(colKey, w);
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize · double-click to auto-fit"
      onMouseDown={onMouseDown}
      onDoubleClick={(e) => { e.stopPropagation(); onLive(colKey, null); onResize && onResize(colKey, null); }}
      onClick={(e) => e.stopPropagation()}
      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-20 group/rs flex justify-end"
      data-testid={`col-resize-${colKey}`}
    >
      <span className="w-px h-full bg-border group-hover/rs:bg-primary group-hover/rs:w-0.5" />
    </span>
  );
}

function ResizableHead({ col, width, className, onResize, onLive, children }) {
  const ref = useRef(null);
  const style = width ? { width, minWidth: width, maxWidth: width } : undefined;
  return (
    <TableHead ref={ref} className={`relative ${className}`} style={style} data-testid={`col-head-${col.key}`}>
      <div className="truncate pr-1">{children}</div>
      <ResizeHandle colKey={col.key} thRef={ref} onResize={onResize} onLive={onLive} />
    </TableHead>
  );
}

export function TableLayout({ records, columns, tableColumns, columnWidths, onColumnResize, onAddColumn, selected, onToggle, onToggleAll, catsById, tagsById, onEdit, onDelete, stockKeys }) {
  const allSelected = records.length > 0 && records.every((r) => selected.has(r.id));
  // Without explicit table columns (e.g. callers that don't manage prefs),
  // fall back to the fields + Category + Tags.
  const cols = tableColumns || [
    ...columns,
    { key: "__category", label: "Category", pseudo: true },
    { key: "__tags", label: "Tags", pseudo: true },
  ];
  // Live widths while dragging; persisted widths come from props.
  const [live, setLive] = useState({});
  useEffect(() => { setLive({}); }, [columnWidths]);
  const widthOf = (key) => (key in live ? live[key] : (columnWidths || {})[key]);
  const onLive = (key, w) => setLive((prev) => ({ ...prev, [key]: w }));
  const cellStyle = (key) => {
    const w = widthOf(key);
    return w ? { width: w, minWidth: w, maxWidth: w } : undefined;
  };
  // Header cells are sticky so they stay pinned while only the rows scroll
  // (the scroll region is the RecordsPage content area). `overflow-hidden`
  // is intentionally omitted here — it would trap position:sticky.
  const headClass = "sticky top-0 z-10 bg-white";
  return (
    <div className="rounded-lg border border-border bg-white w-max min-w-full" data-testid="layout-table">
      <Table wrapperClassName="overflow-visible">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className={`w-10 ${headClass}`}>
              <Checkbox
                checked={allSelected}
                onCheckedChange={onToggleAll}
                data-testid="select-all"
              />
            </TableHead>
            <TableHead className={`w-28 ${headClass}`}>Record #</TableHead>
            {cols.map((c) => (
              <ResizableHead key={c.key} col={c} width={widthOf(c.key)} className={headClass}
                onResize={onColumnResize} onLive={onLive}>
                {c.label}
              </ResizableHead>
            ))}
            {onAddColumn && (
              <TableHead className={`w-10 ${headClass}`}>
                <button type="button" onClick={onAddColumn} title="Add column"
                  className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10"
                  data-testid="table-add-column">
                  <Plus className="w-4 h-4" />
                </button>
              </TableHead>
            )}
            <TableHead className={`text-right w-32 ${headClass}`}>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((r) => (
            <TableRow key={r.id} data-testid={`record-row-${r.record_number}`}>
              <TableCell>
                <Checkbox
                  checked={selected.has(r.id)}
                  onCheckedChange={() => onToggle(r.id)}
                  data-testid={`select-${r.record_number}`}
                />
              </TableCell>
              <TableCell className="font-mono text-xs">
                <div className="flex items-center gap-1.5">
                  <Link to={`/records/${r.id}`} className="text-primary hover:underline" data-testid={`open-record-${r.record_number}`}>
                    {r.record_number}
                  </Link>
                  <LowStockBadge record={r} stockKeys={stockKeys} />
                </div>
              </TableCell>
              {cols.map((c) => (
                <TableCell key={c.key} style={cellStyle(c.key)}
                  className={widthOf(c.key) ? "truncate" : "max-w-[220px] truncate"}>
                  {c.key === "__category" ? <CategoriesInline record={r} catsById={catsById} />
                    : c.key === "__tags" ? <TagsInline record={r} tagsById={tagsById} />
                    : formatCellValue(c, r.fields?.[c.key])}
                </TableCell>
              ))}
              {onAddColumn && <TableCell />}
              <TableCell className="text-right">
                <div className="inline-flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(r)} data-testid={`edit-record-${r.record_number}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => onDelete(r)} data-testid={`delete-record-${r.record_number}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function GalleryLayout({ records, columns, fields, selected, onToggle, catsById, tagsById, onEdit, stockKeys }) {
  // Cover = main photo of the first image field, so a catalogue shows its pictures.
  const imageField = (fields || columns || []).find((f) => f.type === "image");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="layout-gallery">
      {records.map((r) => (
        <Card key={r.id} className="group relative hover:shadow-md transition-shadow" data-testid={`gallery-card-${r.record_number}`}>
          <div className="absolute top-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
            <Checkbox checked={selected.has(r.id)} onCheckedChange={() => onToggle(r.id)}
              className="bg-white/90 border-border" data-testid={`select-${r.record_number}`} />
          </div>
          <div className="absolute top-2 right-2 z-10">
            <LowStockBadge record={r} stockKeys={stockKeys} />
          </div>
          {(() => {
            const cover = imageField && mediaIds(r.fields?.[imageField.key])[0];
            return cover ? (
              <Link to={`/records/${r.id}`} className="block aspect-video bg-muted overflow-hidden rounded-t-lg">
                <MediaCover mediaId={cover} alt={r.title || ""} />
              </Link>
            ) : (
              <div className="aspect-video bg-gradient-to-br from-primary/5 via-muted to-muted/50 flex items-center justify-center text-6xl font-bold text-primary/20 font-mono select-none">
                #{r.record_number.replace(/^[A-Z]+-0*/, "")}
              </div>
            );
          })()}
          <CardHeader className="pb-2">
            <CardTitle className="text-base truncate">
              <Link to={`/records/${r.id}`} className="hover:underline" data-testid={`open-record-${r.record_number}`}>
                {r.title || r.record_number}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-3 space-y-1.5">
            {columns.slice(0, 2).map((c) => (
              <div key={c.id} className="text-xs">
                <span className="text-muted-foreground">{c.label}: </span>
                <span>{formatCellValue(c, r.fields?.[c.key])}</span>
              </div>
            ))}
            <TagsInline record={r} tagsById={tagsById} max={4} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function GridLayout({ records, columns, selected, onToggle, catsById, tagsById, onEdit }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2" data-testid="layout-grid">
      {records.map((r) => (
        <Link
          key={r.id}
          to={`/records/${r.id}`}
          className="group border border-border rounded-lg bg-white p-3 hover:border-primary/50 hover:shadow-sm transition-all relative"
          data-testid={`grid-card-${r.record_number}`}
        >
          <div className="absolute top-1.5 left-1.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(r.id); }}>
            <Checkbox checked={selected.has(r.id)} onCheckedChange={() => onToggle(r.id)} className="bg-white/90 border-border" data-testid={`select-${r.record_number}`} />
          </div>
          <div className="font-mono text-[10px] text-primary">{r.record_number}</div>
          <div className="text-sm font-medium mt-1 truncate">{r.title || "—"}</div>
          <TagsInline record={r} tagsById={tagsById} max={2} />
        </Link>
      ))}
    </div>
  );
}

export function CardLayout({ records, columns, selected, onToggle, catsById, tagsById, onEdit, stockKeys }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="layout-card">
      {records.map((r) => (
        <Card key={r.id} className="hover:shadow-md transition-shadow relative" data-testid={`card-card-${r.record_number}`}>
          <div className="absolute top-3 left-3 z-10">
            <Checkbox checked={selected.has(r.id)} onCheckedChange={() => onToggle(r.id)} data-testid={`select-${r.record_number}`} />
          </div>
          <CardHeader className="pb-2 pl-10">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-[10px] text-primary">{r.record_number}</div>
                <CardTitle className="text-base truncate">
                  <Link to={`/records/${r.id}`} className="hover:underline" data-testid={`open-record-${r.record_number}`}>
                    {r.title || "—"}
                  </Link>
                </CardTitle>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <LowStockBadge record={r} stockKeys={stockKeys} />
                <CategoriesInline record={r} catsById={catsById} />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pl-10 pt-0 space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              {columns.slice(0, 4).map((c) => (
                <div key={c.id} className="min-w-0">
                  <div className="text-muted-foreground text-[10px] font-mono uppercase">{c.label}</div>
                  <div className="truncate">{formatCellValue(c, r.fields?.[c.key])}</div>
                </div>
              ))}
            </div>
            <TagsInline record={r} tagsById={tagsById} max={5} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function ListLayout({ records, columns, selected, onToggle, catsById, tagsById, onEdit, onDelete }) {
  return (
    <div className="divide-y divide-border rounded-lg border border-border bg-white overflow-hidden" data-testid="layout-list">
      {records.map((r) => (
        <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40" data-testid={`list-item-${r.record_number}`}>
          <Checkbox checked={selected.has(r.id)} onCheckedChange={() => onToggle(r.id)} data-testid={`select-${r.record_number}`} />
          <div className="font-mono text-xs text-primary w-24 shrink-0">{r.record_number}</div>
          <div className="flex-1 min-w-0">
            <Link to={`/records/${r.id}`} className="font-medium text-sm truncate block hover:underline" data-testid={`open-record-${r.record_number}`}>
              {r.title || "—"}
            </Link>
            {columns[0] && (
              <div className="text-xs text-muted-foreground truncate">
                {formatCellValue(columns[0], r.fields?.[columns[0].key])}
              </div>
            )}
          </div>
          <div className="hidden md:block"><TagsInline record={r} tagsById={tagsById} max={3} /></div>
          <div className="hidden lg:block w-40"><CategoriesInline record={r} catsById={catsById} /></div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(r)} data-testid={`edit-record-${r.record_number}`}><Pencil className="w-4 h-4" /></Button>
        </div>
      ))}
    </div>
  );
}

export const LAYOUTS = [
  { key: "table", label: "Table" },
  { key: "kanban", label: "Board" },
  { key: "calendar", label: "Calendar" },
  { key: "gallery", label: "Gallery" },
  { key: "grid", label: "Grid" },
  { key: "card", label: "Card" },
  { key: "list", label: "List" },
];

/* ─────────────────────────── Calendar ─────────────────────────── */
const _MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const _DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function parseDay(v) {
  if (!v || typeof v !== "string") return null;
  const m = v.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) { const d = new Date(v); return Number.isNaN(+d) ? null : d; }
  return new Date(+m[1], +m[2] - 1, +m[3]);
}

export function CalendarLayout({ records, fields = [], config = {}, onEdit }) {
  const dateField = config.date_field
    || fields.find((f) => f.type === "date" || f.type === "datetime")?.key;
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });

  if (!dateField) {
    return <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
      This collection has no date field to place records on a calendar.
    </div>;
  }

  // Bucket records by yyyy-mm-dd within the visible month.
  const byDay = {};
  for (const r of records) {
    const d = parseDay(r.fields?.[dateField]);
    if (!d || d.getFullYear() !== cursor.y || d.getMonth() !== cursor.m) continue;
    (byDay[d.getDate()] = byDay[d.getDate()] || []).push(r);
  }

  const first = new Date(cursor.y, cursor.m, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const shift = (delta) => setCursor((c) => {
    const nm = c.m + delta;
    return { y: c.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
  });
  const titleField = fields.find((f) => f.type === "text")?.key;

  return (
    <div className="rounded-lg border border-border bg-white" data-testid="layout-calendar">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <div className="font-semibold">{_MONTHS[cursor.m]} {cursor.y}</div>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-8" onClick={() => setCursor({ y: today.getFullYear(), m: today.getMonth() })}>Today</Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(-1)}><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(1)}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-[11px] font-medium text-muted-foreground border-b border-border">
        {_DOW.map((d) => <div key={d} className="px-2 py-1.5 text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const isToday = d && cursor.y === today.getFullYear() && cursor.m === today.getMonth() && d === today.getDate();
          return (
            <div key={i} className={`min-h-[92px] border-b border-r border-border p-1.5 ${d ? "" : "bg-muted/20"}`}>
              {d && (
                <>
                  <div className={`text-[11px] mb-1 ${isToday ? "font-bold text-primary" : "text-muted-foreground"}`}>{d}</div>
                  <div className="space-y-1">
                    {(byDay[d] || []).slice(0, 4).map((r) => (
                      <button key={r.id} onClick={() => onEdit && onEdit(r)}
                        className="block w-full text-left truncate rounded bg-primary/10 text-primary px-1.5 py-0.5 text-[11px] hover:bg-primary/20">
                        {(titleField && r.fields?.[titleField]) || r.title || `#${r.record_number}`}
                      </button>
                    ))}
                    {(byDay[d] || []).length > 4 && (
                      <div className="text-[10px] text-muted-foreground pl-1">+{byDay[d].length - 4} more</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RecordsLayoutRenderer({ layout, ...props }) {
  switch (layout) {
    case "kanban": return <KanbanLayout {...props} />;
    case "calendar": return <CalendarLayout {...props} />;
    case "gallery": return <GalleryLayout {...props} />;
    case "grid": return <GridLayout {...props} />;
    case "card": return <CardLayout {...props} />;
    case "list": return <ListLayout {...props} />;
    case "table":
    default: return <TableLayout {...props} />;
  }
}

/* ─────────────────────────── Kanban / board ─────────────────────────── */
function KanbanCard({ record, title, meta, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: record.id });
  const style = {
    transform: transform ? `translate3d(${transform.x}px,${transform.y}px,0)` : undefined,
    opacity: isDragging ? 0.45 : 1,
  };
  return (
    <div
      ref={setNodeRef} style={style} {...attributes} {...listeners}
      onClick={() => onEdit && onEdit(record)}
      className="rounded-lg border border-border bg-white p-3 shadow-sm cursor-grab active:cursor-grabbing hover:border-primary/40 transition-colors"
      data-testid={`kanban-card-${record.id}`}
    >
      <div className="text-sm font-medium leading-snug">{title}</div>
      {meta.length > 0 && <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{meta}</div>}
    </div>
  );
}

function KanbanColumn({ id, label, count, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`w-[264px] shrink-0 rounded-xl border p-2.5 transition-colors ${isOver ? "border-primary bg-primary/5" : "border-border bg-muted/30"}`}
      data-testid={`kanban-col-${id}`}
    >
      <div className="flex items-center justify-between px-1 pb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground/80">{label}</span>
        <span className="text-[11px] font-mono text-muted-foreground">{count}</span>
      </div>
      <div className="flex flex-col gap-2 min-h-[44px]">{children}</div>
    </div>
  );
}

const NONE_COL = "__none__";

export function KanbanLayout({ records, fields = [], config = {}, onEdit, onMove }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const groupBy = config.group_by
    || (fields.find((f) => f.type === "dropdown" || f.type === "single_select") || {}).key;
  const groupField = fields.find((f) => f.key === groupBy);
  const options = (groupField?.config?.options) || [];
  const titleField = fields.find((f) => ["text", "email", "url", "phone"].includes(f.type));
  const dueField = fields.find((f) => f.type === "date");
  const priField = fields.find((f) => f.key === "priority")
    || fields.find((f) => (f.type === "dropdown") && f.key !== groupBy);

  if (!groupField) {
    return (
      <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="kanban-needs-field">
        The board groups cards by a dropdown field. Add one (e.g. <b>Status</b>) to this collection to use it.
      </div>
    );
  }

  const columns = [...options.map((o) => ({ id: o, label: o })),
    { id: NONE_COL, label: `No ${groupField.label.toLowerCase()}` }];
  const buckets = {}; columns.forEach((c) => { buckets[c.id] = []; });
  records.forEach((r) => {
    const v = r.fields?.[groupBy];
    buckets[v != null && options.includes(v) ? v : NONE_COL].push(r);
  });

  const titleOf = (r) => r.title || (titleField ? r.fields?.[titleField.key] : null) || r.record_number || "Untitled";

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over) return;
    const target = over.id === NONE_COL ? null : over.id;
    const cur = records.find((r) => r.id === active.id)?.fields?.[groupBy] ?? null;
    if (cur === target) return;
    onMove && onMove(active.id, groupBy, target);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-3" data-testid="layout-kanban">
        {columns.map((c) => (
          <KanbanColumn key={c.id} id={c.id} label={c.label} count={buckets[c.id].length}>
            {buckets[c.id].map((r) => {
              const meta = [];
              const pri = priField && r.fields?.[priField.key];
              if (pri) meta.push(
                <span key="pri" className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{pri}</span>);
              const due = dueField && r.fields?.[dueField.key];
              if (due) meta.push(<span key="due" className="text-[10px] text-muted-foreground font-mono">{due}</span>);
              return <KanbanCard key={r.id} record={r} title={titleOf(r)} meta={meta} onEdit={onEdit} />;
            })}
          </KanbanColumn>
        ))}
      </div>
    </DndContext>
  );
}
